import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import GoogleCalendarSync from "../components/GoogleCalendarSync";
import { TimePicker } from "../components/ui/time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  Filter,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { scheduleService } from "../services/scheduleService";
import { customerService } from "../services/customerService";
import { projectService } from "../services/projectService";
import { userService } from "../services/userService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { formatDateLongPST, formatDatePST } from "../utils/dateUtils";
import { parseApiError } from "../utils/errorUtils";

const APPOINTMENT_TYPE_OPTIONS = [
  { value: "initial_appointment", label: "Initial Appointment" },
  { value: "site_consultation", label: "Site Consultation" },
  { value: "design_review", label: "Design Review" },
  { value: "proposal_presentation", label: "Proposal Presentation" },
  { value: "contract_signing", label: "Contract Signing" },
  { value: "project_walkthrough", label: "Project Walkthrough" },
  { value: "follow_up", label: "Follow-up" },
  { value: "other", label: "Other" }
];

export default function Schedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const routerNavigate = useNavigate();
  
  const [schedule, setSchedule] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week"); // week, month
  const [filterType, setFilterType] = useState("all");
  const [newTask, setNewTask] = useState({
    title: "",
    project_id: "",
    customer_id: "",
    date: "",
    start_time: "",
    end_time: "",
    team: [],
    type: "project",
    address: "",
    assigned_to: "" // User ID of person task is assigned to
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [isEditAppointmentOpen, setIsEditAppointmentOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [dayDetailsDialog, setDayDetailsDialog] = useState({ open: false, date: null, tasks: [] });
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);

  // API hooks
  // Calculate date range based on current view
  const getDateRange = () => {
    if (viewMode === 'month') {
      // Get first and last day of current month
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      return {
        date_from: start.toISOString().split('T')[0],
        date_to: end.toISOString().split('T')[0]
      };
    } else {
      // Week view - get Sunday to Saturday of current week (matching getWeekDates)
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - currentDate.getDay()); // Go back to Sunday
      
      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Add 6 days to get Saturday
      
      return {
        date_from: start.toISOString().split('T')[0],
        date_to: end.toISOString().split('T')[0]
      };
    }
  };

  const { data: scheduleData, isLoading: loading, error } = useQuery({
    queryKey: ['schedule', currentDate, viewMode],
    queryFn: () => scheduleService.getAll(getDateRange())
  });
  
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getAll()
  });
  
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getAll()
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll()
  });

  // Mutations
  const createTaskMutation = useMutation({
    mutationFn: (data) => {
      // Clean up the data - convert empty strings to undefined for optional fields
      // Add seconds to time format if not present (Pydantic expects HH:MM:SS)
      const formatTime = (timeStr) => {
        if (!timeStr) return undefined;
        // If already has seconds, return as is
        if (timeStr.split(':').length === 3) return timeStr;
        // Add :00 for seconds
        return `${timeStr}:00`;
      };
      
      const cleanedData = {
        title: data.title,
        date: data.date || undefined, // Convert empty string to undefined
        start_time: formatTime(data.start_time), // Convert to HH:MM:SS
        end_time: formatTime(data.end_time), // Convert to HH:MM:SS
        type: data.type,
        project_id: data.project_id || undefined,
        customer_id: data.customer_id || undefined,
        address: data.address || undefined,
        team: data.team && data.team.length > 0 ? data.team : undefined,
        notes: data.notes || undefined
      };
      console.log("Sending schedule data:", cleanedData);
      return scheduleService.create(cleanedData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      toast({ description: "Task created successfully!" });
      setNewTask({
        title: "",
        project_id: "",
        customer_id: "",
        date: "",
        start_time: "",
        end_time: "",
        team: [],
        type: "project",
        address: ""
      });
      setIsAddTaskOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to create task")
      });
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => scheduleService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      toast({ description: "Task updated successfully!" });
      setIsTaskDetailOpen(false);
      setSelectedTask(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to update task")
      });
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId) => scheduleService.delete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      toast({ description: "Task deleted successfully!" });
      setDeleteConfirm(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to delete task")
      });
    }
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: ({ customerId, appointmentTitle, appointment }) => 
      customerService.updateAppointment(customerId, appointmentTitle, appointment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Appointment updated and reschedule logged!" });
      setIsEditAppointmentOpen(false);
      setIsTaskDetailOpen(false);
      setEditingAppointment(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to update appointment")
      });
    }
  });

  // Helper function to determine if a task can be deleted
  const isTaskDeletable = (taskId) => {
    // Only customer appointments and regular schedule items can be deleted
    // Project start and payment items are generated and cannot be deleted directly
    return taskId.startsWith('customer_appointment_') || 
           (!taskId.startsWith('project_start_') && !taskId.startsWith('payment_'));
  };

  // Helper function to format time consistently in 12-hour format
  const formatTime = (timeString) => {
    if (!timeString || timeString === null || timeString === undefined) return "";
    
    try {
      // Ensure timeString is a string
      const timeStr = String(timeString);
      
      // Handle different time formats: "HH:MM" (24-hour) or "H:MM AM/PM" (12-hour)
      if (timeStr.includes('AM') || timeStr.includes('PM')) {
        return timeStr; // Already in 12-hour format
      }
      
      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeStr.split(':');
      if (!hours || !minutes) return "";
      
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      
      return `${hour12}:${minutes} ${period}`;
    } catch (error) {
      return ""; // Return empty string if formatting fails
    }
  };

  // Helper functions to handle different data formats
  const getTaskStartTime = (task) => formatTime(task.start_time || task.startTime || "");
  const getTaskEndTime = (task) => formatTime(task.end_time || task.endTime || "");
  const getTaskProject = (task) => task.project || task.project_id || "";
  const getTaskCustomer = (task) => task.customer || task.customer_id || "";

  // Handle task click to navigate to customer or project
  const handleTaskClick = (task) => {
    // Check if it's a callback
    if (task.type === "callback" || task.source === "callback") {
      const cbId = task.callback_id || (task.id && task.id.startsWith('callback_') ? task.id.replace('callback_', '') : null);
      if (cbId) {
        routerNavigate(`/callbacks?openId=${cbId}`);
        return;
      }
    }

    // Check if it's a customer appointment
    if (task.type === "customer" || task.source === "customer_appointment") {
      // Extract customer ID from the task
      let customerId = task.customer_id;
      if (!customerId && task.id && task.id.startsWith('customer_appointment_')) {
        // Parse customer ID from the appointment ID format: customer_appointment_{customerId}_{title}
        const parts = task.id.split('_');
        if (parts.length >= 3) {
          customerId = parts[2];
        }
      }
      if (customerId) {
        routerNavigate(`/customers?openId=${customerId}`);
        return;
      }
    }
    
    // Check if it's a project task
    if (task.type === "project" && task.project_id) {
      routerNavigate(`/projects?openId=${task.project_id}`);
      return;
    }
    
    // Fallback to showing task detail dialog for other types
    setSelectedTask(task);
    setIsTaskDetailOpen(true);
  };

  const handleEditAppointment = () => {
    if (!selectedTask) return;
    
    // Check if this is a customer appointment
    if (selectedTask.id && selectedTask.id.startsWith('customer_appointment_')) {
      const parts = selectedTask.id.split('_');
      if (parts.length >= 3) {
        const customerId = parts[2];
        
        // Set up editing state
        setEditingAppointment({
          customerId: customerId,
          originalTitle: selectedTask.title,
          title: selectedTask.title,
          description: selectedTask.notes || '',
          scheduled_date: selectedTask.date,
          scheduled_time: selectedTask.start_time,
          appointment_type: selectedTask.appointment_type || 'initial_appointment'
        });
        setIsEditAppointmentOpen(true);
      }
    } else {
      toast({
        variant: "destructive",
        description: "Only customer appointments can be edited from this view"
      });
    }
  };

  const handleUpdateAppointment = () => {
    if (!editingAppointment) return;
    
    updateAppointmentMutation.mutate({
      customerId: editingAppointment.customerId,
      appointmentTitle: editingAppointment.originalTitle,
      appointment: {
        title: editingAppointment.title,
        description: editingAppointment.description,
        scheduled_date: editingAppointment.scheduled_date,
        scheduled_time: editingAppointment.scheduled_time,
        appointment_type: editingAppointment.appointment_type
      }
    });
  };

  const handleDeleteTask = async (taskId, taskTitle) => {
    try {
      console.log(`Starting delete operation for: "${taskTitle}"`);
      console.log(`Task ID: ${taskId}`);
      
      // Check if this item can be deleted
      if (taskId.startsWith('customer_appointment_')) {
        // Customer appointments can be deleted
        const parts = taskId.split('_');
        if (parts.length >= 3) {
          const customerId = parts[2]; // customer_appointment_{customerId}_{appointmentTitle}
          
          console.log(`Deleting customer appointment: "${taskTitle}" for customer: ${customerId}`);
          
          // Use the actual task title shown to the user, not the one from the ID
          await customerService.deleteAppointment(customerId, taskTitle);
          queryClient.invalidateQueries({ queryKey: ['schedule'] });
          queryClient.invalidateQueries({ queryKey: ['customers'] });
        }
      } else if (taskId.startsWith('project_start_')) {
        // Project start items are generated from project automation - cannot be deleted directly
        console.log('Cannot delete project start items - they are generated from project data');
        alert('Project start events cannot be deleted directly. To remove this event, update the project status or delete the project.');
        return; // Exit early, don't refresh data
      } else if (taskId.startsWith('payment_')) {
        // Payment items are generated from payment automation - cannot be deleted directly  
        console.log('Cannot delete payment items - they are generated from payment automation');
        alert('Payment due events cannot be deleted directly. They are automatically generated based on project payment schedules.');
        return; // Exit early, don't refresh data
      } else {
        // Try to delete as regular schedule item
        console.log('Attempting to delete as regular schedule item');
        deleteTaskMutation.mutate(taskId);
      }
      
      console.log('Delete API call completed successfully');
      console.log(`Successfully deleted task: "${taskTitle}"`);
      
    } catch (error) {
      console.error('Failed to delete task:', error);
      
      // If it's a 404 error, the appointment was already deleted
      if (error.response && error.response.status === 404) {
        console.log('Appointment was already deleted - refreshing data');
        queryClient.invalidateQueries({ queryKey: ['schedule'] });
        console.log('Data refresh completed after 404');
      } else {
        console.log('Non-404 error occurred:', error.message);
        alert('Failed to delete task. Please try again.');
      }
    } finally {
      // Always close the dialog, regardless of success or failure
      console.log('Closing delete confirmation dialog');
      setDeleteConfirm(null);
    }
  };

  // Get status color for tasks
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Format team members for display
  const formatTeamMembers = (team) => {
    if (!team || team.length === 0) return "No team assigned";
    if (Array.isArray(team)) return team.join(", ");
    return team;
  };

  // Handle showing all tasks for a specific day
  const handleShowDayDetails = (date, tasks) => {
    setDayDetailsDialog({
      open: true,
      date: date,
      tasks: tasks
    });
  };

  // Handle project selection
  const handleProjectChange = (projectId) => {
    // Convert "none" to empty string for backend compatibility
    const actualProjectId = projectId === "none" ? "" : projectId;
    setNewTask(prev => ({ ...prev, project_id: actualProjectId }));
  };

  // Handle customer selection and auto-populate address
  const handleCustomerChange = (customerId) => {
    // Convert "none" to empty string for backend compatibility
    const actualCustomerId = customerId === "none" ? "" : customerId;
    setNewTask(prev => ({ ...prev, customer_id: actualCustomerId }));
    
    // Auto-populate address if customer has one
    if (customerId && customerId !== "none" && customersData?.data) {
      const selectedCustomer = customersData.data.find(customer => 
        (customer.id || customer._id) === customerId
      );
      if (selectedCustomer && selectedCustomer.address && !newTask.address) {
        setNewTask(prev => ({ ...prev, address: selectedCustomer.address }));
      }
    }
  };

  // Update schedule when data is loaded
  useEffect(() => {
    if (scheduleData?.data && scheduleData.data.length > 0) {
      setSchedule(scheduleData.data);
    } else {
      // No fallback - use empty array if no API data
      setSchedule([]);
    }
  }, [scheduleData]);

  const getTypeColor = (type) => {
    switch (type) {
      case 'project':
        return 'bg-blue-100 text-blue-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      case 'lead':
        return 'bg-yellow-100 text-yellow-800';
      case 'service_call':
        return 'bg-red-100 text-red-800';
      case 'callback':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    if (type === 'service_call') return 'Service Call';
    if (type === 'callback') return 'Callback';
    if (type === 'contract_signing') return 'Contract Signing';
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : type;
  };

  const getWeekDates = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Start from Sunday of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    
    // End at Saturday of the week containing the last day
    const endDate = new Date(lastDay);
    endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
    
    const dates = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);
  
  const filteredSchedule = schedule.filter(task => {
    if (filterType === "all") return true;
    return task.type === filterType;
  });

  const getTasksForDate = (date) => {
    // Use local date string to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return filteredSchedule.filter(task => task.date === dateStr);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigate = (direction) => {
    if (viewMode === "week") {
      navigateWeek(direction);
    } else {
      navigateMonth(direction);
    }
  };

  const handleAddTask = async () => {
    // Validate required fields
    if (!newTask.title || !newTask.title.trim()) {
      toast({
        variant: "destructive",
        description: "Task title is required"
      });
      return;
    }

    if (!newTask.date) {
      toast({
        variant: "destructive",
        description: "Date is required"
      });
      return;
    }

    if (!newTask.start_time) {
      toast({
        variant: "destructive",
        description: "Start time is required"
      });
      return;
    }

    if (!newTask.end_time) {
      toast({
        variant: "destructive",
        description: "End time is required"
      });
      return;
    }

    createTaskMutation.mutate(newTask);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Schedule</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage team schedules and project tasks</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
          <Button onClick={() => setIsAddTaskOpen(true)} className="bg-brand-600 hover:bg-brand-700 h-10">
            <Plus className="mr-2 h-4 w-4" />
            Add Task
          </Button>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-40 h-10">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="project">Projects</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="callback">Callbacks</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="service_call">Service Calls</SelectItem>
            </SelectContent>
          </Select>

          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
            <Button
              size="sm"
              variant={viewMode === "week" ? "default" : "ghost"}
              onClick={() => setViewMode("week")}
              className={`px-3 h-10 flex-1 sm:flex-none ${viewMode === "week" ? "bg-white shadow-sm" : ""}`}
            >
              Week
            </Button>
            <Button
              size="sm"
              variant={viewMode === "month" ? "default" : "ghost"}
              onClick={() => setViewMode("month")}
              className={`px-3 h-10 flex-1 sm:flex-none ${viewMode === "month" ? "bg-white shadow-sm" : ""}`}
            >
              Month
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" onClick={() => navigate(-1)} className="h-10 w-10 p-0 shrink-0">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-sm sm:text-xl font-semibold text-center min-w-0 truncate">
              {viewMode === "week"
                ? <>
                    <span className="hidden sm:inline">{currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} - Week View</span>
                    <span className="sm:hidden">{currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Week</span>
                  </>
                : <>
                    <span className="hidden sm:inline">{currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })} - Month View</span>
                    <span className="sm:hidden">{currentDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })} - Month</span>
                  </>
              }
            </h2>
            <Button variant="outline" onClick={() => navigate(1)} className="h-10 w-10 p-0 shrink-0">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      {viewMode === "week" ? (
        /* Week Calendar - horizontally scrollable on mobile */
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
          <div className="flex gap-2 sm:gap-4 sm:grid sm:grid-cols-4 lg:grid-cols-7 min-w-[900px] sm:min-w-0">
            {weekDates.map((date, index) => {
              const dayTasks = getTasksForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();

              return (
                <Card key={index} className={`min-w-[200px] sm:min-w-0 flex-shrink-0 sm:flex-shrink ${isToday ? "ring-2 ring-emerald-500" : ""}`}>
                  <CardHeader className="p-2 sm:pb-3 sm:px-4 sm:pt-4">
                    <CardTitle className="text-sm text-center">
                      <div className="font-medium">
                        <span className="sm:hidden">{['S','M','T','W','T','F','S'][date.getDay()]}</span>
                        <span className="hidden sm:inline">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                      </div>
                      <div className={`text-lg ${isToday ? 'text-brand-600 font-bold' : ''}`}>
                        {date.getDate()}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 sm:p-4 pt-0">
                    <div className="space-y-2 min-h-[200px] sm:min-h-[300px]">
                      {dayTasks.map((task) => (
                        <div
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          className="p-2 border rounded-lg hover:shadow-md hover:bg-gray-50 cursor-pointer transition-all active:bg-gray-100"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-1 min-w-0">
                              <Badge className={`${getStatusColor(task.status)} text-white text-xs shrink-0`}>
                                {task.status}
                              </Badge>
                              <Badge variant="outline" className={`${getTypeColor(task.type)} shrink-0`}>
                                {getTypeLabel(task.type)}
                              </Badge>
                            </div>
                            {isTaskDeletable(task.id) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm({ id: task.id, title: task.title });
                                }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                                title="Delete appointment"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="text-xs font-medium text-gray-900 mb-1 truncate">
                            {task.title}
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div className="flex items-center truncate">
                              <Users className="mr-1 h-3 w-3 shrink-0" />
                              <span className="truncate">{getTaskCustomer(task)}</span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="mr-1 h-3 w-3 shrink-0" />
                              {getTaskStartTime(task)} - {getTaskEndTime(task)}
                            </div>
                            <div className="flex items-center truncate">
                              <MapPin className="mr-1 h-3 w-3 shrink-0" />
                              <span className="truncate">{task.address ? task.address.split(',')[0] : 'No address'}</span>
                            </div>
                            {task.team && task.team.length > 0 && (
                              <div className="text-xs text-gray-500 truncate">
                                Team: {formatTeamMembers(task.team)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {dayTasks.length === 0 && (
                        <div className="text-center text-gray-400 text-xs sm:text-sm py-4 sm:py-8">
                          No tasks
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ) : (
        /* Month Calendar */
        <Card>
          <CardContent className="p-2 sm:pt-6 sm:p-6">
            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                <div key={day} className="p-1 sm:p-2 text-center text-xs sm:text-sm font-medium text-gray-500">
                  <span className="sm:hidden">{['S','M','T','W','T','F','S'][i]}</span>
                  <span className="hidden sm:inline">{day}</span>
                </div>
              ))}
            </div>

            {/* Month calendar grid */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {monthDates.map((date, index) => {
                const dayTasks = getTasksForDate(date);
                const isToday = date.toDateString() === new Date().toDateString();
                const isCurrentMonth = date.getMonth() === currentDate.getMonth();

                return (
                  <div
                    key={index}
                    className={`
                      p-1 sm:p-2 min-h-[52px] sm:min-h-[80px] border rounded sm:rounded-lg cursor-pointer transition-all hover:shadow-md active:bg-gray-100
                      ${isToday ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''}
                      ${isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'}
                    `}
                  >
                    <div className={`text-xs sm:text-sm font-medium mb-0.5 sm:mb-1 ${isToday ? 'text-brand-600' : ''}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-0.5 sm:space-y-1">
                      {dayTasks.slice(0, 2).map((task, taskIndex) => {
                        const isCallback = task.type === "callback" || task.source === "callback";
                        const taskBg = isCallback ? "bg-red-600 hover:bg-red-700" : "bg-brand-600 hover:bg-brand-700";
                        return (
                        <div
                          key={taskIndex}
                          className={`text-xs p-0.5 sm:p-1 rounded text-white ${taskBg} truncate transition-colors relative group`}
                          title={`${task.title} - ${getTaskCustomer(task)}`}
                        >
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskClick(task);
                            }}
                            className="cursor-pointer pr-4"
                          >
                            {task.title}
                          </div>
                          {isTaskDeletable(task.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm({ id: task.id, title: task.title });
                              }}
                              className="absolute top-0 right-0 p-0.5 text-white hover:text-red-200 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete appointment"
                            >
                              <Trash2 className="h-2.5 w-2.5" />
                            </button>
                          )}
                        </div>
                        );
                      })}
                      {dayTasks.length > 2 && (
                        <button
                          onClick={() => handleShowDayDetails(date, dayTasks)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer hover:underline transition-colors py-0.5"
                        >
                          +{dayTasks.length - 2}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Tasks Summary */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <Calendar className="mr-2 h-5 w-5 text-brand-600" />
            Today's Tasks
          </CardTitle>
          <CardDescription>
            Tasks scheduled for today
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="space-y-3">
            {getTasksForDate(new Date()).map((task) => (
              <div key={task.id} className="border rounded-lg p-3 sm:p-4 space-y-2">
                {/* Time - prominent at top */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold flex items-center text-gray-700">
                    <Clock className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    {getTaskStartTime(task)} - {getTaskEndTime(task)}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge className={getTypeColor(task.type)}>
                      {getTypeLabel(task.type)}
                    </Badge>
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${getStatusColor(task.status)}`}></div>
                  </div>
                </div>
                {/* Title and details */}
                <div className="min-w-0">
                  <div className="text-base font-medium truncate">{task.title}</div>
                  <div className="text-sm text-muted-foreground truncate">{getTaskCustomer(task)} - {getTaskProject(task)}</div>
                </div>
                {/* Location */}
                <div className="text-sm text-muted-foreground truncate flex items-center">
                  <MapPin className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                  {task.address ? task.address.split(',')[0] : 'No address'}
                </div>
                {/* Action buttons row */}
                <div className="flex items-center gap-3 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTaskClick(task)}
                    className="h-10 flex-1 sm:flex-none"
                  >
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    View Details
                  </Button>
                  {isTaskDeletable(task.id) && (
                    <button
                      onClick={() => setDeleteConfirm({ id: task.id, title: task.title })}
                      className="h-10 w-10 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                      title="Delete appointment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {getTasksForDate(new Date()).length === 0 && (
              <div className="text-center text-gray-500 py-8 text-sm">
                No tasks scheduled for today
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* All Scheduled Events This Week/Month */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <Calendar className="mr-2 h-5 w-5 text-brand-600" />
            All Scheduled Events This {viewMode === "week" ? "Week" : "Month"}
          </CardTitle>
          <CardDescription>
            {viewMode === "week" 
              ? (() => {
                  // Get week range for description
                  const curr = new Date(currentDate);
                  const day = curr.getDay();
                  const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
                  const weekStart = new Date(curr.setDate(diff));
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);
                  return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                })()
              : currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            } - All appointments and events
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="space-y-3">
            {(() => {
              // Get date range based on view mode
              let rangeStart, rangeEnd;
              
              if (viewMode === "week") {
                // Week view - get Monday to Sunday
                const curr = new Date(currentDate);
                const day = curr.getDay();
                const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
                rangeStart = new Date(curr.setDate(diff));
                rangeEnd = new Date(rangeStart);
                rangeEnd.setDate(rangeStart.getDate() + 6);
              } else {
                // Month view
                rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
              }
              
              const allTasks = [];
              
              // Collect all tasks from each day of the range
              for (let date = new Date(rangeStart); date <= rangeEnd; date.setDate(date.getDate() + 1)) {
                const dayTasks = getTasksForDate(new Date(date));
                dayTasks.forEach(task => {
                  allTasks.push({
                    ...task,
                    scheduledDate: new Date(date)
                  });
                });
              }
              
              // Sort by date and time
              allTasks.sort((a, b) => {
                const dateA = a.scheduledDate;
                const dateB = b.scheduledDate;
                if (dateA.getTime() !== dateB.getTime()) {
                  return dateA - dateB;
                }
                // If same date, sort by time
                const timeA = getTaskStartTime(a);
                const timeB = getTaskStartTime(b);
                return timeA.localeCompare(timeB);
              });
              
              return allTasks.length > 0 ? (
                allTasks.map((task, index) => (
                  <div key={`${task.id}-${index}`} className="border rounded-lg p-3 sm:p-4 hover:shadow-md hover:bg-gray-50 active:bg-gray-100 transition-all space-y-2">
                    {/* Date and time - prominent at top */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold flex items-center text-gray-700 gap-1.5">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        {task.scheduledDate.toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                        <span className="text-muted-foreground font-normal">
                          {getTaskStartTime(task)} - {getTaskEndTime(task)}
                        </span>
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge className={getTypeColor(task.type)}>
                          {task.type}
                        </Badge>
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${getStatusColor(task.status)}`}></div>
                      </div>
                    </div>
                    {/* Title and project */}
                    <div className="min-w-0">
                      <div className="text-base font-medium truncate">{task.title}</div>
                      <div className="text-sm text-muted-foreground truncate">
                        {getTaskCustomer(task)} - {getTaskProject(task)}
                      </div>
                    </div>
                    {/* Location */}
                    <div className="text-sm text-muted-foreground truncate flex items-center">
                      <MapPin className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                      {task.address ? task.address.split(',')[0] : 'No address'}
                    </div>
                    {/* Action buttons row */}
                    <div className="flex items-center gap-3 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTaskClick(task)}
                        className="h-10 flex-1 sm:flex-none"
                      >
                        View
                      </Button>
                      {isTaskDeletable(task.id) && (
                        <button
                          onClick={() => setDeleteConfirm({ id: task.id, title: task.title })}
                          className="h-10 w-10 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                          title="Delete appointment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No events scheduled for {currentDate.toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Google Calendar Integration */}
      <GoogleCalendarSync userId="admin" />

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Appointment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="h-10 flex-1 sm:flex-none">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteTask(deleteConfirm?.id, deleteConfirm?.title)}
              className="h-10 flex-1 sm:flex-none"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <Dialog open={isTaskDetailOpen} onOpenChange={setIsTaskDetailOpen}>
        <DialogContent className="max-h-[100dvh] w-screen md:max-w-2xl md:max-h-[90vh] p-0 gap-0 overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 p-4 sm:p-6 border-b">
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="truncate">{selectedTask?.title}</span>
              <Badge className={`${getStatusColor(selectedTask?.status)} text-white shrink-0`}>
                {selectedTask?.status}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Task details and information
            </DialogDescription>
          </DialogHeader>

          {selectedTask && (
            <div className="space-y-6 p-4 sm:p-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Type</Label>
                  <div className="mt-1">
                    <Badge variant="outline" className={getTypeColor(selectedTask.type)}>
                      {getTypeLabel(selectedTask.type)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Date</Label>
                  <div className="mt-1 text-sm">
                    {formatDateLongPST(selectedTask.date)}
                  </div>
                </div>
                {(getTaskStartTime(selectedTask) || getTaskEndTime(selectedTask)) && (
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Time</Label>
                    <div className="mt-1 text-sm flex items-center">
                      <Clock className="mr-2 h-4 w-4 text-gray-400" />
                      {getTaskStartTime(selectedTask)} - {getTaskEndTime(selectedTask)}
                    </div>
                  </div>
                )}
                {(() => {
                  const startStr = getTaskStartTime(selectedTask);
                  const endStr = getTaskEndTime(selectedTask);
                  if (!startStr || !endStr) return null;
                  const start = new Date(`2000-01-01T${startStr}`);
                  const end = new Date(`2000-01-01T${endStr}`);
                  const hours = (end - start) / (1000 * 60 * 60);
                  if (isNaN(hours)) return null;
                  return (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Duration</Label>
                      <div className="mt-1 text-sm">{hours} hours</div>
                    </div>
                  );
                })()}
              </div>

              {/* Project and Customer Information */}
              <div className="space-y-4">
                {getTaskCustomer(selectedTask) && (
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Customer</Label>
                    <div className="mt-1 text-sm flex items-center">
                      <Users className="mr-2 h-4 w-4 text-gray-400" />
                      {getTaskCustomer(selectedTask)}
                    </div>
                  </div>
                )}
                
                {getTaskProject(selectedTask) && (
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Project</Label>
                    <div className="mt-1 text-sm">
                      {getTaskProject(selectedTask)}
                    </div>
                  </div>
                )}
                
                {selectedTask.address && (
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Address</Label>
                    <div className="mt-1 text-sm flex items-center">
                      <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                      {selectedTask.address}
                    </div>
                  </div>
                )}
              </div>

              {/* Team Information */}
              {selectedTask.team && selectedTask.team.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">Team Members</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(Array.isArray(selectedTask.team) ? selectedTask.team : [selectedTask.team]).map((member, index) => (
                      <Badge key={index} variant="secondary">
                        {member}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Assignment Information */}
              {(selectedTask.assigned_to || selectedTask.created_by) && (
                <div className="border-t pt-4 space-y-3">
                  {selectedTask.assigned_to && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Assigned To</Label>
                      <div className="mt-1 text-sm flex items-center">
                        <Users className="mr-2 h-4 w-4 text-gray-400" />
                        {(() => {
                          const assignedUser = usersData?.data?.find(u => (u.id || u._id) === selectedTask.assigned_to);
                          return assignedUser ? `${assignedUser.name}${assignedUser.email ? ` (${assignedUser.email})` : ''}` : 'Unknown User';
                        })()}
                      </div>
                    </div>
                  )}
                  {selectedTask.created_by && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Created By</Label>
                      <div className="mt-1 text-sm flex items-center text-gray-600">
                        <User className="mr-2 h-4 w-4 text-gray-400" />
                        {(() => {
                          const creator = usersData?.data?.find(u => (u.id || u._id) === selectedTask.created_by);
                          return creator ? `${creator.name}${creator.email ? ` (${creator.email})` : ''}` : 'Unknown User';
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedTask.notes && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">Notes</Label>
                  <div className="mt-1 text-sm bg-gray-50 p-3 rounded-md">
                    {selectedTask.notes}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="sticky bottom-0 bg-white z-10 p-4 sm:p-6 border-t gap-2">
            <Button variant="outline" onClick={() => setIsTaskDetailOpen(false)} className="h-10 flex-1 sm:flex-none">
              Close
            </Button>
            {selectedTask?.id?.startsWith('customer_appointment_') && (
              <Button onClick={handleEditAppointment} className="h-10 flex-1 sm:flex-none">
                Edit Task
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Details Dialog */}
      <Dialog open={dayDetailsDialog.open} onOpenChange={(open) => setDayDetailsDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-h-[100dvh] w-screen md:max-w-2xl md:max-h-[90vh] p-0 gap-0 overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 p-4 sm:p-6 border-b">
            <DialogTitle className="text-lg">
              {dayDetailsDialog.date && (
                <>
                  <span className="hidden sm:inline">{`All Tasks for ${dayDetailsDialog.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}`}</span>
                  <span className="sm:hidden">{dayDetailsDialog.date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              View all scheduled items for this day
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 p-4 sm:p-6">
            {dayDetailsDialog.tasks.map((task, index) => (
              <div
                key={`${task.id}-${index}`}
                className="p-3 border rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Badge className={`${getStatusColor(task.status)} text-white text-xs px-2 py-1`}>
                        {getTypeLabel(task.type) || 'Task'}
                      </Badge>
                      {task.start_time && task.end_time && (
                        <span className="text-sm text-gray-500">
                          {formatTime(task.start_time)} - {formatTime(task.end_time)}
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">{task.title}</h4>
                    
                    {task.address && (
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <MapPin className="h-4 w-4 mr-1" />
                        {task.address}
                      </div>
                    )}
                    
                    {task.team && task.team.length > 0 && (
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <Users className="h-4 w-4 mr-1" />
                        {formatTeamMembers(task.team)}
                      </div>
                    )}
                    
                    {task.description && (
                      <p className="text-sm text-gray-600">{task.description}</p>
                    )}
                  </div>
                  
                  {isTaskDeletable(task.id) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDeleteConfirm({ id: task.id, title: task.title });
                        setDayDetailsDialog(prev => ({ ...prev, open: false }));
                      }}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 h-10 w-10 p-0 shrink-0"
                      title="Delete appointment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {dayDetailsDialog.tasks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No tasks scheduled for this day
              </div>
            )}
          </div>

          <DialogFooter className="sticky bottom-0 bg-white z-10 p-4 sm:p-6 border-t">
            <Button
              variant="outline"
              onClick={() => setDayDetailsDialog(prev => ({ ...prev, open: false }))}
              className="h-10 w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={isEditAppointmentOpen} onOpenChange={setIsEditAppointmentOpen}>
        <DialogContent className="max-h-[100dvh] w-screen md:max-w-lg md:max-h-[90vh] p-0 gap-0 overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 p-4 sm:p-6 border-b">
            <DialogTitle>Edit Appointment</DialogTitle>
            <DialogDescription>Update appointment details. Changes will be logged as a reschedule.</DialogDescription>
          </DialogHeader>
          {editingAppointment && (
            <div className="space-y-4 p-4 sm:p-6">
              <div className="space-y-2">
                <Label htmlFor="editAppointmentTitle">Title *</Label>
                <Input
                  id="editAppointmentTitle"
                  value={editingAppointment.title}
                  onChange={(e) => setEditingAppointment({...editingAppointment, title: e.target.value})}
                  placeholder="Site consultation..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editAppointmentType">Type *</Label>
                <Select
                  value={editingAppointment.appointment_type}
                  onValueChange={(value) => setEditingAppointment({...editingAppointment, appointment_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editAppointmentDescription">Description</Label>
                <Textarea
                  id="editAppointmentDescription"
                  value={editingAppointment.description}
                  onChange={(e) => setEditingAppointment({...editingAppointment, description: e.target.value})}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editAppointmentDate">Date *</Label>
                  <Input
                    id="editAppointmentDate"
                    type="date"
                    value={editingAppointment.scheduled_date}
                    onChange={(e) => setEditingAppointment({...editingAppointment, scheduled_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editAppointmentTime">Time *</Label>
                  <TimePicker
                    id="editAppointmentTime"
                    value={editingAppointment.scheduled_time}
                    onChange={(value) => setEditingAppointment({...editingAppointment, scheduled_time: value})}
                    placeholder="Select time"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="sticky bottom-0 bg-white z-10 p-4 sm:p-6 border-t gap-2">
            <Button variant="outline" onClick={() => setIsEditAppointmentOpen(false)} className="h-10 flex-1 sm:flex-none">
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAppointment}
              disabled={updateAppointmentMutation.isPending || !editingAppointment?.title.trim() || !editingAppointment?.scheduled_date || !editingAppointment?.scheduled_time}
              className="bg-brand-600 hover:bg-brand-700 h-10 flex-1 sm:flex-none"
            >
              {updateAppointmentMutation.isPending ? "Updating..." : "Update Appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={isAddTaskOpen} onOpenChange={setIsAddTaskOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Schedule Task</DialogTitle>
            <DialogDescription>Create a new task on the schedule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newTask.type} onValueChange={(value) => setNewTask({...newTask, type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="service_call">Service Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={newTask.project_id || undefined} onValueChange={(value) => setNewTask({...newTask, project_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {(projectsData?.data?.data || []).map(p => (
                      <SelectItem key={p.id || p._id} value={p.id || p._id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Customer</Label>
                <Select value={newTask.customer_id || undefined} onValueChange={(value) => setNewTask({...newTask, customer_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {(customersData?.data?.data || []).map(c => (
                      <SelectItem key={c.id || c._id} value={c.id || c._id}>{c.name} {c.last_name || ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newTask.date}
                onChange={(e) => setNewTask({...newTask, date: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <TimePicker
                  value={newTask.start_time}
                  onChange={(value) => setNewTask({...newTask, start_time: value})}
                  placeholder="Start time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <TimePicker
                  value={newTask.end_time}
                  onChange={(value) => setNewTask({...newTask, end_time: value})}
                  placeholder="End time"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                placeholder="Job site address"
                value={newTask.address}
                onChange={(e) => setNewTask({...newTask, address: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsAddTaskOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!newTask.title?.trim() || !newTask.date || !newTask.start_time || !newTask.end_time) {
                  toast({ variant: "destructive", description: "Please fill in title, date, start time, and end time" });
                  return;
                }
                createTaskMutation.mutate(newTask);
              }}
              disabled={createTaskMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {createTaskMutation.isPending ? "Creating..." : "Create Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}